import PageContent from '@/components/PageContent';
import { AdvanceShareStyleUsageAudienceTypeList, AdvanceShareStyleUsagePeriodTypeList, AdvanceShareStyleUsageRuleDefinition } from '@/models/advance-share-style-definition.model';
import { AdvanceShareStyleVersion, AdvanceShareStyleVersionStatusEnum, UpsertAdvanceShareStyleVersion } from '@/models/advance-share-style-version.model';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { upsertAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { AxiosError } from 'axios';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Schema,
  SelectPicker,
  NumberInput,
  Stack,
  Divider,
  IconButton,
  getHeight,
} from 'rsuite';
import { useNavigate } from 'react-router-dom';

const { Column, HeaderCell, Cell } = Table;

interface AdvanceShareStyleVersionUsageRulesProps {
  formObject?: AdvanceShareStyleVersion;
  fetchData: () => void;
}

const AdvanceShareStyleVersionUsageRules: React.FC<AdvanceShareStyleVersionUsageRulesProps> = ({ formObject, fetchData }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | undefined>(undefined);
  const [editingUsageRule, setEditingUsageRule] = useState<AdvanceShareStyleUsageRuleDefinition | undefined>(undefined);
  const [value, setValue] = useState<AdvanceShareStyleUsageRuleDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const nav = useNavigate();

  const model = Schema.Model({
    audience: Schema.Types.NumberType().isRequired('Type is required'),
    period: Schema.Types.NumberType().isRequired('Type is required'),
    limit: Schema.Types.NumberType().isRequired('Type is required'),
    priority: Schema.Types.NumberType().isRequired('Type is required'),
  });

  useEffect(() => {
    if(formObject){
      setValue(formObject.usageRules ?? []);
      setDisabled(!!formObject && formObject.status != AdvanceShareStyleVersionStatusEnum.Draft);
    } else {
      setValue([]);
      setDisabled(false);
    }
  }, [formObject]);
  
  const handleUpdateStyleVersion = async (updatedValue: AdvanceShareStyleUsageRuleDefinition[]) => {
    setLoading(true);
    try {
      const castedModel: UpsertAdvanceShareStyleVersion = { 
        ...formObject, usageRules: updatedValue.length == 0 ? undefined : updatedValue
      };      
      const result: BaseResponseModel<any> = await upsertAdvanceShareStyleVersion(castedModel);
      if (result.success) {
        toastSuccessMessage("Usage rules successfully updated.");
        fetchData();
      }
    } catch (err) {
      const errorMsg = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage("Error on updating style version usage rules: " + errorMsg);
    }finally{
      setLoading(false);
    }    
  };

  const handleModalSubmit = (formValue: AdvanceShareStyleUsageRuleDefinition) => {
    if(value.some((item, index) => 
      item.audience === formValue.audience && 
      item.period === formValue.period && 
      index !== editingRowIndex)){
        // validate unique key
        toastErrorMessage('Duplicated usage rule audience and period.');
        return;
    }


  let updated: AdvanceShareStyleUsageRuleDefinition[];
  if (editingRowIndex !== undefined) {
    updated = [...value];
    updated[editingRowIndex] = formValue;
  } else {
    updated = [...value, formValue];
  }
    
    handleUpdateStyleVersion(updated);
    setShowModal(false);
    setEditingUsageRule(undefined);
  };

  const handleEditRow = (rowData: AdvanceShareStyleUsageRuleDefinition, rowIndex?: number) => {
    setEditingUsageRule(rowData);
    setShowModal(true);
    setEditingRowIndex(rowIndex);
  };
    
  const handleDeleteRow = (rowIndex?: number) => {
    const updated = [...value];
    updated.splice(rowIndex!, 1);
    setValue(updated);
    handleUpdateStyleVersion(updated);
  };
  
  const handleFormChange = (value: Record<string, any>) => {
    setEditingUsageRule(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };


  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between"> 
        <Button appearance="primary" disabled = {disabled || loading} onClick={() => {
            setShowModal(true);
            setEditingUsageRule({ audience: undefined, period: undefined, limit: undefined, priority: undefined });
            setEditingRowIndex(undefined);
        }}>
          Add Rule
        </Button>
      </Stack>

      <Table data={value}  height={Math.max(getHeight(window) - 350, 400)}
>
        <Column flexGrow={1}>
          <HeaderCell>Audience</HeaderCell>
          <Cell>
            {rowData =>
              AdvanceShareStyleUsageAudienceTypeList.findLast(t => t.value === rowData.audience)?.label
            }
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Period</HeaderCell>
          <Cell>
            {rowData =>
              AdvanceShareStyleUsagePeriodTypeList.findLast(t => t.value === rowData.period)?.label
            }
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Limit</HeaderCell>
          <Cell dataKey="limit" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Priority</HeaderCell>
          <Cell dataKey="priority" />
        </Column>
        <Column width={120}>
          <HeaderCell>Actions</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareStyleUsageRuleDefinition, rowIndex?: number) => (
              <Stack spacing={6}>
                  <Button 
                  size="xs" 
                  appearance='ghost' 
                  disabled = {disabled || loading}
                  onClick={() => handleEditRow(rowData, rowIndex)} >
                      Edit
                  </Button>
                  <Button
                      color='red'
                      appearance='ghost'
                      size="xs"
                      disabled = {disabled || loading}
                      onClick={() => handleDeleteRow(rowIndex)}
                  >
                  X
                  </Button>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      
      <Divider />

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
      </Stack>

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title>
            {editingRowIndex !== undefined ? 'Edit Usage Rule' : 'Add Usage Rule'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            fluid
            model={model}
            formValue={editingUsageRule}
            onChange={handleFormChange}
            onSubmit={formValue => handleModalSubmit(formValue as AdvanceShareStyleUsageRuleDefinition)}
          >
            <Form.Group controlId="audience">
              <Form.Label>Audience</Form.Label>
              <Form.Control
                name="audience"
                accepter={SelectPicker}
                data={AdvanceShareStyleUsageAudienceTypeList}
                block
              />
            </Form.Group>
            <Form.Group controlId="audience">
              <Form.Label>Period</Form.Label>
              <Form.Control
                name="period"
                accepter={SelectPicker}
                data={AdvanceShareStyleUsagePeriodTypeList}
                block
              />
            </Form.Group>
            <Form.Group controlId="limit">
              <Form.Label>Limit</Form.Label>
              <Form.Control name="limit" accepter={NumberInput}/>
            </Form.Group>
            <Form.Group controlId="priority">
              <Form.Label>Priority</Form.Label>
              <Form.Control name="priority" accepter={NumberInput}/>
            </Form.Group>
            <Button appearance="primary" type="submit">
              Save
            </Button>
          </Form>
        </Modal.Body>
      </Modal>        
    </PageContent>
  );
};

export default AdvanceShareStyleVersionUsageRules;
